/* ==========================================================================
   AVICOO — HEADER BEHAVIOUR
   Visual Task 01 · Sticky states, transparent-to-solid transition
   --------------------------------------------------------------------------
   Registers into window.AVICOO. Creates no new global.
   Replaces Dawn's inline StickyHeader custom element.

   Two mutually exclusive paths:
     Standard  — Dawn's position:sticky + negative top. No layout jump.
     Overlay   — position:absolute over the hero, switching to fixed + solid.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.AVICOO) return;

  var SCROLL_KEY = '_avcHeaderState';

  function measure(state) {
    var section = state.section;
    var wrapper = state.wrapper;

    var headerHeight = wrapper.offsetHeight;
    document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
    state.headerHeight = headerHeight;

    if (state.isOverlay) {
      var bar = document.querySelector('.announcement-bar-section');
      var offset = bar ? bar.offsetHeight : 0;
      section.style.setProperty('--avc-header-top', offset + 'px');
      state.overlayTop = offset;
    }

    var rect = section.getBoundingClientRect();
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    state.headerBottom = rect.bottom + scrollTop;
  }

  function applyStandard(state, scrollTop) {
    var section = state.section;

    if (state.alwaysSticky) {
      section.classList.add('shopify-section-header-sticky');
      if (scrollTop > state.headerBottom) {
        section.classList.add('scrolled-past-header');
      } else {
        section.classList.remove('scrolled-past-header');
      }
      return;
    }

    if (state.stickyType === 'none') return;

    var goingDown = scrollTop > state.lastScroll;

    if (scrollTop <= state.headerBottom) {
      section.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate',
        'scrolled-past-header'
      );
      return;
    }

    section.classList.add('scrolled-past-header');

    /* HeaderMenu (details-disclosure.js) sets preventHide on .header-wrapper
       while a dropdown is open. Honour it or menus close themselves. */
    if (goingDown) {
      if (state.wrapper.preventHide) return;
      section.classList.add('shopify-section-header-sticky', 'shopify-section-header-hidden');
      closeOpenDisclosures(state);
    } else {
      section.classList.add('shopify-section-header-sticky', 'animate');
      section.classList.remove('shopify-section-header-hidden');
    }
  }

  function applyOverlay(state, scrollTop) {
    var section = state.section;
    var threshold = state.headerHeight + state.overlayTop;

    if (scrollTop > threshold) {
      section.classList.add('avc-is-pinned');
    } else {
      section.classList.remove('avc-is-pinned', 'avc-is-hidden');
      return;
    }

    if (state.stickyType === 'none') {
      section.classList.remove('avc-is-pinned');
      return;
    }

    if (state.alwaysSticky) return;

    var goingDown = scrollTop > state.lastScroll;

    if (goingDown) {
      if (state.wrapper.preventHide) return;
      section.classList.add('avc-is-hidden');
      closeOpenDisclosures(state);
    } else {
      section.classList.remove('avc-is-hidden');
    }
  }

  function closeOpenDisclosures(state) {
    var menus = state.section.querySelectorAll('header-menu');
    for (var i = 0; i < menus.length; i++) {
      if (typeof menus[i].close === 'function') menus[i].close();
    }
    var modal = state.section.querySelector('details-modal');
    if (modal && typeof modal.close === 'function') modal.close(false);
  }

  function onScroll(state) {
    if (state.ticking) return;
    state.ticking = true;

    window.requestAnimationFrame(function () {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      /* Never move the header while predictive search is open. */
      var search = state.section.querySelector('predictive-search');
      if (search && search.isOpen) {
        state.lastScroll = scrollTop;
        state.ticking = false;
        return;
      }

      if (state.isOverlay) {
        applyOverlay(state, scrollTop);
      } else {
        applyStandard(state, scrollTop);
      }

      state.lastScroll = scrollTop < 0 ? 0 : scrollTop;
      state.ticking = false;
    });
  }

  function onResize(state) {
    if (state.resizeTicking) return;
    state.resizeTicking = true;

    window.requestAnimationFrame(function () {
      measure(state);
      state.resizeTicking = false;
    });
  }

  function init(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var wrapper = root.querySelector('.avc-header-wrapper');
    if (!wrapper) return;

    var section = wrapper.closest('.section-header');
    if (!section || section[SCROLL_KEY]) return;

    var state = {
      section: section,
      wrapper: wrapper,
      stickyType: wrapper.getAttribute('data-sticky-type') || 'none',
      isOverlay: section.classList.contains('avc-overlay'),
      lastScroll: 0,
      headerHeight: 0,
      headerBottom: 0,
      overlayTop: 0,
      ticking: false,
      resizeTicking: false,
    };

    state.alwaysSticky =
      state.stickyType === 'always' || state.stickyType === 'reduce-logo-size';

    state.scrollHandler = function () {
      onScroll(state);
    };
    state.resizeHandler = function () {
      onResize(state);
    };

    measure(state);

    if (state.alwaysSticky && !state.isOverlay) {
      section.classList.add('shopify-section-header-sticky');
    }

    window.addEventListener('scroll', state.scrollHandler, { passive: true });
    window.addEventListener('resize', state.resizeHandler, { passive: true });

    section[SCROLL_KEY] = state;

    /* Establish the correct state for a page loaded mid-scroll. */
    onScroll(state);
  }

  function destroy(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var wrapper = root.querySelector('.avc-header-wrapper');
    if (!wrapper) return;

    var section = wrapper.closest('.section-header');
    if (!section) return;

    var state = section[SCROLL_KEY];
    if (!state) return;

    window.removeEventListener('scroll', state.scrollHandler);
    window.removeEventListener('resize', state.resizeHandler);
    section[SCROLL_KEY] = null;
  }

  window.AVICOO.register('header', init, destroy);
})();